/**
 * 비속어 필터링 유틸리티
 * - 클라이언트: 경량 체크 (즉시 경고 UX)
 * - 서버: 전체 데이터셋으로 마스킹 처리
 */

export interface ProfanityCheckResult {
    hasProfanity: boolean;
    matchedWords: string[];
}

export interface ProfanityMaskResult {
    originalText: string;
    maskedText: string;
    hasProfanity: boolean;
    matchedWords: string[];
}

/**
 * 텍스트에서 비속어 검출
 * @param text 검사할 텍스트
 * @param wordList 비속어 목록
 * @returns 검출 결과
 */
export function checkProfanity(
    text: string,
    wordList: string[]
): ProfanityCheckResult {
    if (!text || wordList.length === 0) {
        return { hasProfanity: false, matchedWords: [] };
    }

    const normalizedText = normalizeText(text);
    const matchedWords: string[] = [];

    for (const word of wordList) {
        const normalizedWord = normalizeText(word);
        if (normalizedText.includes(normalizedWord)) {
            matchedWords.push(word);
        }
    }

    return {
        hasProfanity: matchedWords.length > 0,
        matchedWords
    };
}

/**
 * 텍스트에서 비속어를 마스킹 처리
 * @param text 원본 텍스트
 * @param wordList 비속어 목록
 * @returns 마스킹된 텍스트와 결과 정보
 */
export function maskProfanity(
    text: string,
    wordList: string[]
): ProfanityMaskResult {
    if (!text || wordList.length === 0) {
        return {
            originalText: text,
            maskedText: text,
            hasProfanity: false,
            matchedWords: []
        };
    }

    let maskedText = text;
    const matchedWords: string[] = [];

    // 긴 단어부터 처리 (부분 매칭 방지)
    const sortedWordList = [...wordList].sort((a, b) => b.length - a.length);

    for (const word of sortedWordList) {
        // 대소문자 무시, 전역 검색
        const regex = createProfanityRegex(word);
        if (regex.test(maskedText)) {
            matchedWords.push(word);
            maskedText = maskedText.replace(regex, (match) =>
                createMask(match.length)
            );
        }
    }

    return {
        originalText: text,
        maskedText,
        hasProfanity: matchedWords.length > 0,
        matchedWords
    };
}

/**
 * 텍스트 정규화 (공백, 특수문자 제거 등)
 */
function normalizeText(text: string): string {
    return text
        .toLowerCase()
        .replace(/\s+/g, "") // 공백 제거
        .replace(/[.\-_]/g, ""); // 일반적인 우회 문자 제거
}

/**
 * 비속어 매칭을 위한 정규식 생성
 * 우회 패턴 대응 (예: 시.발, 시 발, 시-발)
 */
function createProfanityRegex(word: string): RegExp {
    // 각 문자 사이에 선택적 구분자 허용 (공백, 점, 대시, 언더스코어)
    // 최대 1-2개의 구분자만 허용하여 과도한 매칭 방지
    const escapedChars = word.split("").map((char) => escapeRegex(char));
    const pattern = escapedChars.join("[\\s.\\-_]{0,2}");
    return new RegExp(pattern, "gi");
}

/**
 * 정규식 특수문자 이스케이프
 */
function escapeRegex(str: string): string {
    return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * 마스크 문자열 생성
 * 첫 글자는 유지, 나머지는 * 처리
 */
function createMask(length: number): string {
    if (length <= 1) return "*";
    if (length === 2) return "**";
    // 첫 글자 유지 옵션 (선택적)
    return "*".repeat(length);
}
